<template>
	<PageLoading
		:loading="!cases.welcomeCase || user.activating"
		class="relative"
		:class="{ 'pb-[calc(var(--padding-bottom)+76px)]': step === 0 }"
	>
		<PagePadding class="flex flex-col items-center gap-2">
			<div class="text-label-secondary text-xs font-medium uppercase">
				{{ t('welcome') }}
			</div>
			<LogoTextColorIcon class="h-7" />
		</PagePadding>
		<PagePillRow class="mt-8 snap-mandatory *:gap-2">
			<OnboardingStep
				:name="t('freeCase')"
				:value="t('left', [cases.welcomeCase?.count || 0])"
				:active="step === 0"
				:disabled="!cases.welcomeCase?.count"
				@click="setStep(0)"
			>
				<BoxIcon />
			</OnboardingStep>
			<div class="bg-fill-tertiary h-0.5 w-3.5 rounded-full" />
			<OnboardingStep
				:name="t('depositBonus')"
				:value="`+${user.depositBonus ? user.depositBonus : 5}%`"
				:active="step === 1"
				@click="setStep(1)"
			>
				<GiftIcon />
			</OnboardingStep>
		</PagePillRow>
		<CaseEmbed
			class="mt-6"
			v-if="cases.welcomeCase"
			:uuid="cases.welcomeCase.uuid"
			@loaded="loaded"
			v-show="step === 0"
			ref="embed"
			:cantOpen="cantOpen"
			@open="openRequirements()"
			@conditionsNotMet="openRequirements(true)"
			bottomActions
		>
			<template #actionReplace v-if="cases.welcomeCase?.count === 0">
				<BaseButton
					class="flex-1"
					size="lg"
					color="primary"
					@click="setStep(1)"
				>
					{{ t('continue') }}
				</BaseButton>
			</template>
			<template #actionLeft>
				<BaseButton class="flex-1" size="lg" color="secondary" @click="skip()">
					{{ t('skip') }}
				</BaseButton>
			</template>
			<template #hint>
				<div class="inline-flex items-center gap-2">
					<ShieldIcon class="text-yellow h-5 w-5" />
					<div class="text-label-primary text-sm font-medium capitalize">
						{{ t('fair') }}
					</div>
				</div>
				<div
					class="text-label-primary relative text-sm font-medium capitalize after:absolute after:-inset-3"
					v-hover="{ active: 'scale-95', impact: 'soft' }"
					@click="openFairness()"
				>
					{{ t('learnMore') }}
				</div>
			</template>
		</CaseEmbed>
		<template v-if="step === 1">
			<PagePadding>
				<ActivateCode ref="activateCode" class="mt-6" />
				<SubtitleText class="mt-5">
					{{ t('crypto') }}
				</SubtitleText>
				<div class="mt-2 space-y-2">
					<DepositMethodWide
						:color="methods[0].color"
						:name="methods[0].name"
						:desc="methods[0].desc"
						@click="setMethod(methods[0].id)"
					>
						<Component :is="methods[0].icon" />
					</DepositMethodWide>
					<DepositMethodWide
						:color="methods[1].color"
						:name="methods[1].name"
						:desc="methods[1].desc"
						@click="setMethod(methods[1].id)"
					>
						<Component :is="methods[1].icon" />
					</DepositMethodWide>
					<DepositMethodWide
						:color="methods[2].color"
						:name="methods[2].name"
						:desc="methods[2].desc"
						@click="setMethod(methods[2].id)"
					>
						<Component :is="methods[2].icon" />
					</DepositMethodWide>
				</div>
				<SubtitleText class="mt-4">{{ t('more') }}</SubtitleText>
				<DepositMethodWide
					v-if="user.starsEnabled && user.maxStarsDeposit > 0"
					class="mt-2"
					:color="methods[3].color"
					:name="methods[3].name"
					:desc="methods[3].desc"
					@click="setMethod(methods[3].id)"
				>
					<Component :is="methods[3].icon" />
				</DepositMethodWide>
				<DepositMethodWide
					class="mt-2"
					:color="methods[4].color"
					:name="methods[4].name"
					:desc="methods[4].desc"
					soon
				>
					<img class="h-9 w-9" :src="methods[4].icon" />
				</DepositMethodWide>
				<BaseButton
					class="mt-5 w-full"
					size="lg"
					color="secondary"
					@click="skip()"
				>
					{{ t('skip') }}
				</BaseButton>
			</PagePadding>
		</template>
		<RequirementsModal v-model="requirementsModal" />
	</PageLoading>
</template>
<script setup lang="ts">
import { useLayout } from '@/stores/layout'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import PageLoading from '@/components/PageLoading.vue'
import PagePadding from '@/components/PagePadding.vue'
import LogoTextColorIcon from '@/assets/icons/logo-text-color.svg'
import PagePillRow from '@/components/PagePillRow.vue'
import BoxIcon from '@/assets/icons/box.svg'
import GiftIcon from '@/assets/icons/gift.svg'
import OnboardingStep from '@/components/OnboardingStep.vue'
import { useI18n } from 'vue-i18n'
import CaseEmbed from '@/components/CaseEmbed.vue'
import BaseButton from '@/components/BaseButton.vue'
import { useRouter } from 'vue-router'
import ShieldIcon from '@/assets/icons/shield.svg'
import SubtitleText from '@/components/SubtitleText.vue'
import DepositMethodWide from '@/components/DepositMethodWide.vue'
import TonIcon from '@/assets/icons/ton.svg'
import UsdtIcon from '@/assets/icons/usdt.svg'
import CryptoBotIcon from '@/assets/icons/cryptobot.svg'
import StarsIcon from '@/assets/icons/stars.svg'
import RequirementsModal from '@/components/modals/RequirementsModal.vue'
import { useUser } from '@/stores/user'
import { useCases } from '@/stores/cases'
import { useToast } from '@/stores/toast'
import { client } from '@/api/orpc'
import ActivateCode from '@/components/ActivateCode.vue'
import GiftsDepositIcon from '@/assets/illustrations/gifts-deposit.png'

const layout = useLayout()

layout.hideTopNavbar()

onBeforeUnmount(() => layout.showTopNavbar())

layout.showBottomNavbar()

const { t } = useI18n({
	messages: {
		en: {
			freeCase: 'free case',
			left: 'x{0} left',
			depositBonus: 'deposit bonus',
			contains: 'Case Contains',
			welcome: 'Welcome to',
			skip: 'skip',
			fair: 'Provably Fair',
			learnMore: 'learn more',
			bonusApplied: '{0}% Bonus Applied',
			dailyCoins: 'Up to {0} extra coins daily',
			crypto: 'Crypto',
			more: 'More Methods',
			methods: {
				ton: {
					title: 'TON',
					desc: 'direct deposit'
				},
				ton_tether: {
					title: 'USDT',
					desc: 'via ton network'
				},
				cryptobot: {
					title: 'Cryptobot',
					desc: '10+ currencies',
					notice: {
						title: '10+ currencies supported',
						desc: 'You will be prompted to select your target currency at the next step.'
					}
				},
				stars: {
					title: 'Stars',
					desc: 'direct deposit'
				},
				gifts: {
					title: 'Gifts',
					desc: 'direct deposit'
				}
			},
			noFreeCase: 'You do not have any welcome cases available to open.',
			continue: 'continue'
		},
		ru: {
			freeCase: 'бесплатный кейс',
			left: 'еще {0} шт.',
			depositBonus: 'бонус к депозиту',
			contains: 'Кейс содержит',
			welcome: 'Добро пожаловать на',
			skip: 'пропустить',
			fair: 'Честная Игра',
			learnMore: 'узнать больше',
			bonusApplied: '{0}% Бонус Применен',
			dailyCoins: 'До {0} доп. монет ежедневно',
			crypto: 'Крипта',
			more: 'Другие методы',
			methods: {
				ton: {
					title: 'TON',
					desc: 'через сеть ton'
				},
				ton_tether: {
					title: 'USDT',
					desc: 'через сеть ton'
				},
				cryptobot: {
					title: 'Cryptobot',
					desc: '10+ валют',
					notice: {
						title: 'Доступно 10+ валют',
						desc: 'На следующем шаге ты сможешь выбрать нужную валюту.'
					}
				},
				stars: {
					title: 'Звезды',
					desc: 'прямое пополнение'
				},
				gifts: {
					title: 'Гифты',
					desc: 'прямое пополнение'
				}
			},
			noFreeCase: 'У тебя нет доступных бесплатных кейсов для открытия.',
			continue: 'продолжить'
		}
	}
})

const step = ref(0)

const setStep = (index: number) => {
	step.value = index
}

const caseLoading = ref(true)

const loaded = () => {
	caseLoading.value = false
}

const router = useRouter()

const skip = () => {
	router.push('/')
}

const embed = ref<InstanceType<typeof CaseEmbed>>()

const openFairness = () => {
	if (!embed.value) {
		return
	}

	embed.value.openFairness()
}

type DepositMethod = 'ton' | 'ton_tether' | 'cryptobot' | 'stars' | 'gifts'

const methods = computed<
	{
		id: DepositMethod
		name: string
		color: string
		desc: string
		icon: string
		iconOutline?: string
		prefillValues?: number[]
		notice?: {
			title: string
			desc: string
		}
	}[]
>(() => [
	{
		id: 'ton',
		name: t('methods.ton.title'),
		color: '#0088CC',
		desc: t('methods.ton.desc'),
		icon: TonIcon,
		prefillValues: [1000, 5000, 10000, 20000]
	},
	{
		id: 'ton_tether',
		name: t('methods.ton_tether.title'),
		color: '#26A17B',
		desc: t('methods.ton_tether.desc'),
		icon: UsdtIcon,
		prefillValues: [500, 2500, 5000, 10000]
	},
	{
		id: 'cryptobot',
		name: t('methods.cryptobot.title'),
		color: '#0088CC',
		desc: t('methods.cryptobot.desc'),
		icon: CryptoBotIcon,
		notice: {
			title: t('methods.cryptobot.notice.title'),
			desc: t('methods.cryptobot.notice.desc')
		}
	},
	{
		id: 'stars',
		name: t('methods.stars.title'),
		color: '#FFB526',
		desc: t('methods.stars.desc'),
		icon: StarsIcon,
		prefillValues: [50000, 250000, 500000, 1000000]
	},
	{
		id: 'gifts',
		name: t('methods.gifts.title'),
		color: '#DB922B',
		desc: t('methods.gifts.desc'),
		icon: GiftsDepositIcon
	}
])

const activateCode = ref<InstanceType<typeof ActivateCode>>()

const setMethod = (id: DepositMethod) => {
	layout.showDepositModal(
		1000,
		id as 'ton' | 'ton_tether' | 'cryptobot' | 'stars'
	)

	if (!activateCode.value) {
		return
	}

	activateCode.value.resetToOldCode()
}

const cantOpen = computed(
	() => !cases.welcomeCase?.count || !user.isSubscribed || !user.isPremium
)

const toast = useToast()

const requirementsModal = ref(false)

const check = async () => {
	const { error, data, isDefined } =
		await client.user.checkPremiumAndChannelSubscription()

	if (isDefined) {
		if (isDefined && error.code === 'NOT_IN_CHANNEL') {
			user.setPremiumAndSubscription(user.isPremium, false)
			return
		}
	}

	if (error) {
		return console.error(error)
	}

	if (!data) {
		return
	}

	user.setPremiumAndSubscription(data.isPremium, data.isChannelSubscriber)
}

const openRequirements = async (force?: true) => {
	if (!cases.welcomeCase?.count) {
		toast.add({
			text: t('noFreeCases'),
			type: 'error'
		})
		return
	}
	if (user.isSubscribed && user.isPremium && !force) {
		return
	}

	await check()

	requirementsModal.value = true
}

const user = useUser()
const cases = useCases()

watch(
	() => user.uuid,
	() => {
		if (!user.uuid) {
			return
		}

		check()

		if (
			!cases.welcomeCase ||
			user.activatedCode ||
			cases.welcomeCase.count > 0
		) {
			return
		}

		router.replace('/')
	},
	{ immediate: true }
)

watch(
	() => cases.welcomeCase,
	() => {
		if (step.value !== 0) {
			return
		}
		if (!cases.welcomeCase) {
			return
		}
		if (cases.welcomeCase.count) {
			return
		}

		setStep(1)
	},
	{
		immediate: true
	}
)
</script>
